import { useEffect, useState, useRef } from "react";
import { X, Cpu, DollarSign, MapPin, UserCheck, Tag, FileText, Info, ChevronDown, Calendar as CalendarIcon, Check, UserMinus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../connector/api";
import toast from "react-hot-toast";

export default function ITEquipmentModal({ isOpen, onClose, onSubmit, formData, setFormData }) {
  const [users, setUsers] = useState([]);
  
  // Dropdown context menu flags
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [custodianOpen, setCustodianOpen] = useState(false);

  const modalRef = useRef(null);

  const categories = [
    "CCTV Camera",
    "Desktop Computer",
    "Media Equipment",
    "Network Equipment",
    "Projector",
    "Other"
  ];

  // Fetch target domain agents on container activation
  useEffect(() => {
    if (!isOpen) return;

    const fetchUsers = async () => {
      try {
        const res = await api.get("users/all/");
        setUsers(res.data);
      } catch (err) {
        toast.error("Failed to fetch user directory");
      }
    };

    fetchUsers();
  }, [isOpen]);

  // Global surface tracking outside handler
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setCategoryOpen(false);
        setCustodianOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  // Helper selectors mapping layout strings safely
  const selectedUser = users.find(u => String(u.id) === String(formData.responsible_person));
  const custodianLabel = selectedUser 
    ? `${selectedUser.first_name} ${selectedUser.last_name}`
    : "Select Target System User Profile";

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 overflow-y-auto">
          {/* Ambient Backdrop Blur Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-950/40 backdrop-blur-[6px]"
          />

          {/* Premium Form Workspace Surface */}
          <motion.div
            ref={modalRef}
            initial={{ scale: 0.98, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.98, opacity: 0, y: 8 }}
            transition={{ type: "spring", duration: 0.3, bounce: 0 }}
            className="bg-white rounded-xl w-full max-w-2xl shadow-2xl border border-slate-100 relative z-10 my-auto flex flex-col overflow-visible"
          >
            <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-[#002855]/30 to-transparent" />
            {/* Modal Header Modules */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-slate-50/50 rounded-t-2xl">
              <div>
                <h2 className="text-sm font-semibold text-slate-900 tracking-tight flex items-center gap-2">
                  <Cpu size={16} className="text-[#002855]" />
                  {formData.id ? "Modify Asset Logs" : "Register New Hardware Asset"}
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Fill in the operational indices to maintain hardware tracking compliance.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200/50 hover:text-slate-700 transition-colors cursor-pointer"
              >
                <X size={15} />
              </button>
            </div>

            {/* Modal Content Structure */}
            <form onSubmit={onSubmit} className="p-6 space-y-5 text-xs font-medium text-slate-600 overflow-visible">
              
              {/* Main Description Input Layer */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Asset Specification Label</label>
                <div className="relative flex items-center bg-slate-50/50 border border-slate-200 rounded-xl focus-within:ring-2 focus-within:ring-[#002855]/10 focus-within:border-[#002855] focus-within:bg-white transition-all shadow-xs">
                  <FileText size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="e.g., Sony Alpha A7 IV Surveillance Array"
                    value={formData.description || ""}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 bg-transparent font-medium text-slate-800 placeholder-slate-400 outline-none"
                    required
                  />
                </div>
              </div>

              {/* Two Column Form Grid Area */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-visible">
                
                {/* Custom Category Dropdown Component */}
                <div className="space-y-1.5 relative">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Classification Category</label>
                  <button
                    type="button"
                    onClick={() => {
                      setCategoryOpen(!categoryOpen);
                      setCustodianOpen(false);
                    }}
                    className="w-full flex items-center justify-between pl-10 pr-4 py-2 bg-slate-50/50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] hover:bg-white transition-all text-left font-medium text-slate-800 cursor-pointer shadow-xs"
                  >
                    <Tag size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                    <span>{formData.category || "Select Category Block"}</span>
                    <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${categoryOpen ? "rotate-180 text-slate-600" : ""}`} />
                  </button>

                  <AnimatePresence>
                    {categoryOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.12 }}
                        className="absolute left-0 right-0 mt-1.5 bg-white border border-slate-200 shadow-xl rounded-xl py-1 z-50 max-h-48 overflow-y-auto"
                      >
                        {categories.map((cat) => {
                          const isChosen = formData.category === cat;
                          return (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, category: cat });
                                setCategoryOpen(false);
                              }}
                              className={`w-full text-left px-4 py-1.5 text-xs font-medium transition-colors flex items-center justify-between cursor-pointer ${
                                isChosen ? "text-[#002855] bg-[#002855]/5 font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                              }`}
                            >
                              <span>{cat}</span>
                              {isChosen && <Check size={12} className="text-[#002855] stroke-[2.5px]" />}
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Hardware Model Framework */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Model Framework Identifier</label>
                  <div className="relative flex items-center bg-slate-50/50 border border-slate-200 rounded-xl focus-within:ring-2 focus-within:ring-[#002855]/10 focus-within:border-[#002855] focus-within:bg-white transition-all shadow-xs">
                    <Cpu size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="e.g., PTZ-PRO-V4"
                      value={formData.model || ""}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 bg-transparent font-medium text-slate-800 placeholder-slate-400 outline-none"
                      required
                    />
                  </div>
                </div>

                {/* Serial Tracking String Reference */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Hardware Serial Number</label>
                  <div className="relative flex items-center bg-slate-50/50 border border-slate-200 rounded-xl focus-within:ring-2 focus-within:ring-[#002855]/10 focus-within:border-[#002855] focus-within:bg-white transition-all shadow-xs">
                    <span className="absolute left-3.5 text-[10px] font-bold tracking-wider text-slate-400 font-mono select-none">S/N</span>
                    <input
                      type="text"
                      placeholder="e.g., SN-84920-X"
                      value={formData.serial_number || ""}
                      onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                      className="w-full pl-11 pr-4 py-2 bg-transparent font-mono font-medium tracking-wide text-slate-800 placeholder-slate-400 outline-none text-xs"
                      required
                    />
                  </div>
                </div>

                {/* Procurement Cost Numerical Index */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Procurement Cost</label>
                  <div className="relative flex items-center bg-slate-50/50 border border-slate-200 rounded-xl focus-within:ring-2 focus-within:ring-[#002855]/10 focus-within:border-[#002855] focus-within:bg-white transition-all shadow-xs">
                    <DollarSign size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                    <input
                      type="number"
                      placeholder="0.00"
                      min="0"
                      step="any"
                      value={formData.cost || ""}
                      onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 bg-transparent font-medium text-slate-800 placeholder-slate-400 outline-none"
                      required
                    />
                  </div>
                </div>

                {/* Date Input Box */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Acquisition Date</label>
                  <div className="relative flex items-center bg-slate-50/50 border border-slate-200 rounded-xl focus-within:ring-2 focus-within:ring-[#002855]/10 focus-within:border-[#002855] focus-within:bg-white transition-all shadow-xs">
                    <CalendarIcon size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                    <input
                      type="date"
                      value={formData.date_of_purchase || ""}
                      onChange={(e) => setFormData({ ...formData, date_of_purchase: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 bg-transparent font-medium text-slate-800 outline-none cursor-pointer"
                      required
                    />
                  </div>
                </div>

                {/* Operational Zone Location String */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Assigned Operational Zone</label>
                  <div className="relative flex items-center bg-slate-50/50 border border-slate-200 rounded-xl focus-within:ring-2 focus-within:ring-[#002855]/10 focus-within:border-[#002855] focus-within:bg-white transition-all shadow-xs">
                    <MapPin size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="e.g., Perimeter Gate Floor 2"
                      value={formData.location || ""}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 bg-transparent font-medium text-slate-800 placeholder-slate-400 outline-none"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Custodian Selection Node Menu */}
              <div className="space-y-1.5 relative">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Accountable Custodian Agent</label>
                <button
                  type="button"
                  onClick={() => {
                    setCustodianOpen(!custodianOpen);
                    setCategoryOpen(false);
                  }}
                  className="w-full flex items-center justify-between pl-10 pr-4 py-2 bg-slate-50/50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#002855]/10 focus:border-[#002855] hover:bg-white transition-all text-left font-medium text-slate-800 cursor-pointer shadow-xs"
                >
                  <UserCheck size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                  <span className={formData.responsible_person ? "text-slate-800 font-medium" : "text-slate-400 font-normal"}>
                    {custodianLabel}
                  </span>
                  <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${custodianOpen ? "rotate-180 text-slate-600" : ""}`} />
                </button>

                <AnimatePresence>
                  {custodianOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.12 }}
                      className="absolute left-0 right-0 mt-1.5 bg-white border border-slate-200 shadow-xl rounded-xl py-1 z-50 max-h-48 overflow-y-auto"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, responsible_person: "" });
                          setCustodianOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-xs font-normal text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors flex items-center gap-2 cursor-pointer"
                      >
                        <UserMinus size={12} /> Clear Assignment
                      </button>
                      {users.map((user) => {
                        const isChosen = String(formData.responsible_person) === String(user.id);
                        return (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, responsible_person: String(user.id) });
                              setCustodianOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-xs font-medium transition-colors flex items-center justify-between cursor-pointer ${
                              isChosen ? "text-[#002855] bg-[#002855]/5 font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                            }`}
                          >
                            <span>
                              {user.first_name} {user.last_name} <span className="text-[10px] text-slate-400 font-normal ml-1">({user.username || "Agent"})</span>
                            </span>
                            {isChosen && <Check size={12} className="text-[#002855] stroke-[2.5px]" />}
                          </button>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Sub-Footer Layout Actions Footer */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-6">
                <div className="hidden sm:flex items-center gap-1.5 text-[10px] font-medium text-slate-400">
                  <Info size={12} className="text-slate-300 shrink-0" />
                  Fill in parameters cleanly to persist modifications.
                </div>

                <div className="flex justify-end gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 bg-white border border-slate-200 rounded-xl transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 text-xs font-medium text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 rounded-xl shadow-xs transition-all cursor-pointer"
                  >
                    {formData.id ? "Commit Updates" : "Commit Registration"}
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}